import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Typography,
  message
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Amount from "../shared/Amount";
import { formatDate } from "../shared/money";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { errorText, openBlob } from "../api";
import * as api from "./api";
import { useLabels } from "./receivablesUi";

/**
 * CB-4: Kontoauszug / Saldenbestätigung for one customer — customer picker, period (or the
 * date of the confirmation), preview, PDF download and email to the customer.
 */
const StatementDialog = ({
  open,
  onClose,
  customerId: initialCustomer,
  branch: initialBranch = "",
  from,
  to
}) => {
  const { t } = useTranslation("accounting");
  const labels = useLabels();
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(initialCustomer || null);
  // Kontoauszug of one Niederlassung: "" = whole customer, "none" = head office, else branch id
  const [branch, setBranch] = useState("");
  const [mode, setMode] = useState("statement");
  const [range, setRange] = useState([
    from ? dayjs(from) : dayjs().startOf("year"),
    to ? dayjs(to) : dayjs()
  ]);
  const [date, setDate] = useState(dayjs().subtract(1, "year").endOf("year"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open) return;
    setCustomerId(initialCustomer || null);
    setBranch(initialBranch || "");
    api
      .listStatementCustomers()
      .then(res => setCustomers(Array.isArray(res.data?.customers) ? res.data.customers : []))
      .catch(error => message.error(errorText(error)));
  }, [open, initialCustomer, initialBranch]);

  const params = useCallback(
    () =>
      mode === "statement"
        ? {
            from: range[0].format("YYYY-MM-DD"),
            to: range[1].format("YYYY-MM-DD"),
            ...(branch ? { branch } : {})
          }
        : { date: date.format("YYYY-MM-DD") },
    [mode, range, date, branch]
  );

  useEffect(() => {
    if (!open || !customerId) {
      setData(null);
      return;
    }
    setLoading(true);
    const call =
      mode === "statement"
        ? api.getStatement(customerId, params())
        : api.getBalanceConfirmation(customerId, params());
    call
      .then(res => {
        setData(res.data);
        setEmail(res.data?.customer?.email || "");
      })
      .catch(error => message.error(errorText(error)))
      .finally(() => setLoading(false));
  }, [open, customerId, mode, params]);

  const branches = customers.find(c => String(c.id) === String(customerId))?.branches || [];

  const download = async () => {
    setBusy("pdf");
    try {
      const name = data?.customer?.customerNumber || "Kunde";
      await openBlob(
        mode === "statement"
          ? api.downloadStatement(customerId, params())
          : api.downloadBalanceConfirmation(customerId, params()),
        mode === "statement"
          ? `Kontoauszug_${name}.pdf`
          : `Saldenbestaetigung_${name}_${date.format("YYYY-MM-DD")}.pdf`
      );
    } catch (error) {
      message.error(errorText(error));
    } finally {
      setBusy("");
    }
  };

  const send = async () => {
    setBusy("mail");
    try {
      const res = await api.emailStatement(customerId, {
        ...params(),
        confirm: mode === "confirmation",
        email: email || undefined
      });
      message.success(`${t("Sent to")} ${res.data.to}`);
    } catch (error) {
      message.error(errorText(error));
    } finally {
      setBusy("");
    }
  };

  return (
    <Modal
      rootClassName="acct-module"
      open={open}
      onCancel={onClose}
      width={960}
      title={t("Customer statement")}
      footer={
        <Space wrap>
          <Button onClick={onClose}>{t("Close")}</Button>
          <Button disabled={!data} loading={busy === "pdf"} onClick={download}>
            {t("Download PDF")}
          </Button>
          <Button type="primary" disabled={!data} loading={busy === "mail"} onClick={send}>
            {t("Send by email")}
          </Button>
        </Space>
      }
    >
      <Form layout="vertical">
        <Form.Item label={t("Customer")}>
          <Select
            showSearch
            value={customerId}
            placeholder={t("Select a customer")}
            optionFilterProp="label"
            onChange={v => {
              setCustomerId(v);
              setBranch("");
            }}
            options={customers.map(c => ({
              value: c.id,
              label: `${c.name}${c.customerNumber ? ` (${c.customerNumber})` : ""} · ${
                c.debitorAccount
              }`
            }))}
          />
        </Form.Item>
        <Space wrap align="end" className="acct-wrap">
          <Form.Item label={t("Document")}>
            <Radio.Group
              value={mode}
              onChange={e => setMode(e.target.value)}
              optionType="button"
              options={[
                { value: "statement", label: t("Account statement") },
                { value: "confirmation", label: t("Balance confirmation") }
              ]}
            />
          </Form.Item>
          {mode === "statement" && branches.length ? (
            <Form.Item label={t("Branch")}>
              <Select
                style={{ minWidth: 220 }}
                value={branch}
                onChange={setBranch}
                options={[
                  { value: "", label: t("Whole customer") },
                  { value: "none", label: t("Head office (no branch)") },
                  ...branches.map(b => ({
                    value: String(b._id),
                    label: `${b.name}${b.code ? ` (${b.code})` : ""}${
                      b.active ? "" : ` · ${t("Archived")}`
                    }`
                  }))
                ]}
              />
            </Form.Item>
          ) : null}
          {mode === "statement" ? (
            <Form.Item label={t("Period")}>
              <DatePicker.RangePicker
                value={range}
                allowClear={false}
                format="DD.MM.YYYY"
                onChange={v => v && v[0] && v[1] && setRange([v[0], v[1]])}
              />
            </Form.Item>
          ) : (
            <Form.Item label={t("Balance date")}>
              <DatePicker
                value={date}
                allowClear={false}
                format="DD.MM.YYYY"
                onChange={v => v && setDate(v)}
              />
            </Form.Item>
          )}
          <Form.Item label={t("Email")}>
            <Input
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={{ minWidth: 220 }}
            />
          </Form.Item>
        </Space>
      </Form>
      {!customerId ? (
        <Empty description={t("Select a customer")} />
      ) : mode === "statement" ? (
        <>
          {data && !data.verify?.ok ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 8 }}
              message={
                data.branch
                  ? t("The branch statement does not match the branch's open items.")
                  : t("The statement does not match the ledger balance.")
              }
            />
          ) : null}
          {data?.branch && data.unassigned?.count ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 8 }}
              message={
                <>
                  {t(
                    "Bookings without an invoice (e.g. unassigned payments) are only on the whole customer's statement"
                  )}
                  {": "}
                  <Amount cents={data.unassigned.amount} />
                </>
              }
            />
          ) : null}
          <Descriptions size="small" column={{ xs: 1, sm: 3 }} style={{ marginBottom: 8 }}>
            <Descriptions.Item label={t("Opening balance")}>
              <Amount cents={data?.opening} />
            </Descriptions.Item>
            <Descriptions.Item label={t("Closing balance")}>
              <Amount cents={data?.closing} strong />
            </Descriptions.Item>
            <Descriptions.Item label={t("Account")}>
              {data?.customer?.debitorAccount}
            </Descriptions.Item>
          </Descriptions>
          <ResponsiveTable
            mobileLayout="table"
            className="acct-grid"
            size="small"
            loading={loading}
            pagination={false}
            rowKey={r => `${r.entry}`}
            dataSource={data?.rows || []}
            columns={[
              { title: t("Date"), dataIndex: "date", render: formatDate, width: 100 },
              { title: t("Type"), dataIndex: "kind", render: k => labels.kind[k] || k },
              {
                title: t("Document"),
                dataIndex: "document",
                render: (v, r) => (
                  <span>
                    {v}
                    {r.reference ? (
                      <Typography.Text type="secondary" style={{ display: "block" }}>
                        {`→ ${r.reference}`}
                      </Typography.Text>
                    ) : null}
                  </span>
                )
              },
              {
                title: t("Debit"),
                dataIndex: "debit",
                align: "end",
                render: v => (v ? <Amount cents={v} /> : "")
              },
              {
                title: t("Credit"),
                dataIndex: "credit",
                align: "end",
                render: v => (v ? <Amount cents={v} /> : "")
              },
              {
                title: t("Balance"),
                dataIndex: "balance",
                align: "end",
                render: v => <Amount cents={v} />
              }
            ]}
            summary={() =>
              data ? (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <strong>{t("Total")}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="end">
                    <Amount cents={data.totals.debit} strong />
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="end">
                    <Amount cents={data.totals.credit} strong />
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="end">
                    <Amount cents={data.closing} strong />
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              ) : null
            }
          />
          {data?.memos?.length ? (
            <Alert
              type="info"
              style={{ marginTop: 8 }}
              message={t("Without effect on the balance")}
              description={data.memos.map((m, i) => (
                <div key={i} className="acct-wrap">
                  {`${formatDate(m.date)} · ${labels.kind[m.kind] || m.kind} · ${m.document}${
                    m.reference ? ` / ${m.reference}` : ""
                  }`}
                  {m.amount ? (
                    <>
                      {" · "}
                      <Amount cents={m.amount} />
                    </>
                  ) : null}
                </div>
              ))}
            />
          ) : null}
        </>
      ) : (
        <>
          <Descriptions size="small" column={{ xs: 1, sm: 2 }} style={{ marginBottom: 8 }}>
            <Descriptions.Item label={t("Balance")}>
              <Amount cents={data?.balance} strong />
            </Descriptions.Item>
            <Descriptions.Item label={t("Balance date")}>
              {formatDate(data?.date)}
            </Descriptions.Item>
          </Descriptions>
          <ResponsiveTable
            mobileLayout="table"
            size="small"
            loading={loading}
            pagination={false}
            rowKey="item"
            dataSource={data?.items || []}
            columns={[
              { title: t("Document"), dataIndex: "item" },
              { title: t("Date"), dataIndex: "firstDate", render: formatDate },
              {
                title: t("Amount"),
                dataIndex: "amount",
                align: "end",
                render: v => <Amount cents={v} />
              }
            ]}
          />
        </>
      )}
    </Modal>
  );
};

export default StatementDialog;
